import React, { useState, useEffect } from 'react';

const AddWidgetModal = ({
  newWidget: initialWidgetData,
  handleSaveWidget,
  onClose,
  tools, 
  tabs = [],
  activeTab
}) => {
  // Local state for the form
  const [localWidget, setLocalWidget] = useState({
    title: initialWidgetData.title || '',
    widgetType: initialWidgetData.widgetType || 'text',
    tool: initialWidgetData.tool || '',
    toolId: initialWidgetData.toolId || '',
    endpoint: initialWidgetData.endpoint || '',
    prompt: initialWidgetData.prompt || '',
    visualPrompt: initialWidgetData.visualPrompt || '',
    tabId: activeTab || '',
    hasAvatar: initialWidgetData.hasAvatar || false
  });
  
  // Loading state
  const [isSaving, setIsSaving] = useState(false);
  
  // Update local widget when active tab changes
  useEffect(() => {
    if (activeTab) {
      setLocalWidget(prev => ({
        ...prev,
        tabId: activeTab
      }));
    }
  }, [activeTab]);
  
  // Form validation
  const isFormValid = () => {
    if (!localWidget.title || !localWidget.prompt) return false;
    if (localWidget.widgetType === 'visualization' && !localWidget.visualPrompt) return false;
    return true;
  };

  // Handle local input changes
  const handleLocalInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    
    setLocalWidget(prev => ({
      ...prev,
      [name]: inputValue
    }));
    
    // Special handling for tool selection
    if (name === 'tool') {
      const selectedTool = tools.find(tool => tool.name === value);
      if (selectedTool) {
        setLocalWidget(prev => ({
          ...prev,
          toolId: selectedTool.id,
          endpoint: selectedTool.api_endpoint || ''
        }));
      }
    }
  };

  // Handle save - pass the local state to parent
  const handleSave = async () => {
    if (!isFormValid()) return;
    
    setIsSaving(true);
    
    try {
      // Make sure we're using the active tab
      const widgetToSave = {
        ...localWidget,
        tabId: activeTab
      };
      
      // Pass our local state to the parent's save function
      await handleSaveWidget(widgetToSave);
    } catch (error) {
      console.error("Error saving widget:", error);
      alert("Failed to save widget. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[1000] backdrop-blur-sm">
      <div className="bg-white rounded-lg p-6 w-[450px] max-w-[90%] shadow-xl border border-gray-200 animate-fadeInUp">
        <h2 className="mt-0 mb-5 text-xl font-semibold border-b border-gray-100 pb-3">
          Create New Widget
        </h2>
        
        <div className="mb-4">
          <label htmlFor="title" className="block mb-1.5 font-medium text-sm">
            Widget Title:
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={localWidget.title}
            onChange={handleLocalInputChange}
            placeholder="Enter widget title"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-all bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
            required
          />
        </div>
        
        <div className="mb-4">
          <label className="block mb-1.5 font-medium text-sm">Widget Type:</label>
          <div className="flex gap-5 py-2">
            <label className="flex items-center text-sm font-normal cursor-pointer">
              <input
                type="radio"
                name="widgetType"
                value="text"
                checked={localWidget.widgetType === 'text'}
                onChange={handleLocalInputChange}
                className="mr-1.5 accent-secondary cursor-pointer"
              />
              Text Widget
            </label>
            <label className="flex items-center text-sm font-normal cursor-pointer">
              <input
                type="radio"
                name="widgetType"
                value="visualization"
                checked={localWidget.widgetType === 'visualization'}
                onChange={handleLocalInputChange}
                className="mr-1.5 accent-secondary cursor-pointer"
              />
              Visualization Widget
            </label>
          </div>
        </div>
        
        <div className="mb-4">
          <label htmlFor="tool" className="block mb-1.5 font-medium text-sm">
            Tool:
          </label>
          <select
            id="tool"
            name="tool"
            value={localWidget.tool}
            onChange={handleLocalInputChange}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-all bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
          >
            <option value="">Select a tool</option>
            {tools.map(tool => (
              <option key={tool.id} value={tool.name}>
                {tool.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Tab information (read-only) */}
        {/* <div className="mb-4">
          <label className="block mb-1.5 font-medium text-sm">
            Tab:
          </label>
          <div className="px-3 py-2.5 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700">
            {tabs.find(tab => tab.id === activeTab || tab.tab_id === activeTab)?.name || 
             tabs.find(tab => tab.id === activeTab || tab.tab_id === activeTab)?.tab_name || 
             'Current Tab'}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Widget will be added to the currently selected tab
          </p>
        </div> */}
        
        <div className="mb-4">
          <label htmlFor="prompt" className="block mb-1.5 font-medium text-sm">
            Prompt:
          </label>
          <textarea
            id="prompt"
            name="prompt"
            value={localWidget.prompt}
            onChange={handleLocalInputChange}
            placeholder="Enter the prompt for this widget"
            rows="3"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-all bg-gray-50 resize-y min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
            required
          />
        </div>
        
        {localWidget.widgetType === 'visualization' && (
          <div className="mb-4">
            <label htmlFor="visualPrompt" className="block mb-1.5 font-medium text-sm">
              Visualization Prompt:
            </label>
            <textarea
              id="visualPrompt"
              name="visualPrompt"
              value={localWidget.visualPrompt}
              onChange={handleLocalInputChange}
              placeholder="Enter the visualization prompt"
              rows="3"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-all bg-gray-50 resize-y min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
              required={localWidget.widgetType === 'visualization'}
            />
          </div>
        )}
        
        <div className="mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="hasAvatar"
              checked={localWidget.hasAvatar}
              onChange={handleLocalInputChange}
              className="w-4 h-4 accent-secondary cursor-pointer"
            />
            <span className="text-sm font-medium">Enable Avatar for this widget</span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            When enabled, content will be presented with an interactive avatar
          </p>
        </div>
        
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            type="button"
            className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md font-medium text-sm transition-all hover:bg-gray-200"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className={`px-4 py-2 bg-secondary text-white rounded-md font-medium text-sm transition-all ${
              !isFormValid() || isSaving
                ? 'opacity-60 cursor-not-allowed'
                : 'hover:bg-secondaryDark'
            }`}
            disabled={!isFormValid() || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Widget'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddWidgetModal;